import { createClient } from '@supabase/supabase-js'
import { showSessionRecoveryToast } from '@/components/SessionRecoveryToast';


const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

if (!supabaseUrl) {
  throw new Error('Missing VITE_SUPABASE_URL');
}
if (!supabaseAnonKey) {
  throw new Error('Missing VITE_SUPABASE_ANON_KEY');
}

export const supabase = createClient(supabaseUrl, supabaseAnonKey, {
  auth: {
    persistSession: true,
    storage: window.localStorage,
    autoRefreshToken: true,
    detectSessionInUrl: true,
    refreshTokenRotationEnabled: true,
    refreshTokenMargin: 300,
    retryAttempts: 3
  },
  global: {
    headers: {
      'X-Client-Info': 'kaufpause-web'
    }
  },
  realtime: {
    params: {
      eventsPerSecond: 10
    }
  }
});

export const withRetry = async <T>(
  operation: () => Promise<T>,
  maxRetries: number = 2,
  showUserFeedback: boolean = true
): Promise<T> => {
  let lastError: any;
  let hasShownUserFeedback = false;
  
  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      // Timeout für die gesamte Operation
      const operationPromise = (async () => {
      // Spezielle Behandlung für Tab-Wechsel-Recovery
      if (attempt > 0) {
        const tabRecoverySuccess = await handleTabVisibilityChange();
        if (!tabRecoverySuccess && attempt === 1) {
          console.log('🔄 Tab-Recovery fehlgeschlagen, versuche Standard-Session-Prüfung...');
        }
      }
      
        // Session-Check mit Timeout
        const sessionPromise = supabase.auth.getSession();
        const sessionTimeoutPromise = new Promise((_, reject) => 
          setTimeout(() => reject(new Error('Session check timeout')), 8000)
        );
        
        const { data: { session }, error: sessionError } = await Promise.race([
          sessionPromise,
          sessionTimeoutPromise
        ]) as any;
      
      if (sessionError) {
        console.warn(`Session-Fehler bei Versuch ${attempt + 1}:`, sessionError);
        
        if (attempt < maxRetries) {
          const { error: refreshError } = await supabase.auth.refreshSession();
          if (refreshError) {
            console.warn('Token-Refresh fehlgeschlagen:', refreshError);
              throw refreshError;
            } else {
              console.log('Token erfolgreich refreshed');
            }
          } else {
            throw sessionError;
          }
        }
        
        // Operation mit Timeout ausführen
        const operationTimeoutPromise = new Promise((_, reject) => 
          setTimeout(() => reject(new Error('Operation timeout')), 15000)
        );
        
        return await Promise.race([
          operation(),
          operationTimeoutPromise
        ]) as T;
      })();
      
      // Gesamter Timeout für die Operation
      const globalTimeoutPromise = new Promise((_, reject) => 
        setTimeout(() => reject(new Error('Global operation timeout')), 20000)
      );
      
      const result = await Promise.race([
        operationPromise,
        globalTimeoutPromise
      ]) as T;
      
      return result;
      
    } catch (error: any) {
      lastError = error;
      console.warn(`API-Aufruf fehlgeschlagen bei Versuch ${attempt + 1}:`, error);
      
      // Spezielle Behandlung für Timeout-Fehler
      if (error?.message?.includes('timeout')) {
        console.warn('⏰ Timeout-Fehler erkannt:', error.message);
        
        if (attempt === maxRetries && showUserFeedback && !hasShownUserFeedback) {
          hasShownUserFeedback = true;
          console.log('⏰ Timeout nach mehreren Versuchen - Page reload empfohlen');
        }
        
        if (attempt === maxRetries) {
          throw new Error('Operation timed out after multiple attempts');
        }
        
        // Bei Timeout kürzere Wartezeit
        await new Promise(resolve => setTimeout(resolve, 500));
        continue;
      }
      
      if (error?.message?.includes('JWT') || 
          error?.message?.includes('token') || 
          error?.message?.includes('unauthorized') ||
          error?.code === 'PGRST301') {
        
        if (attempt < maxRetries) {
          console.log('Auth-Fehler erkannt, versuche Token-Refresh...');
          
          try {
            const refreshPromise = supabase.auth.refreshSession();
            const refreshTimeoutPromise = new Promise((_, reject) => 
              setTimeout(() => reject(new Error('Refresh timeout')), 5000)
            );
            
            const { error: refreshError } = await Promise.race([
              refreshPromise,
              refreshTimeoutPromise
            ]) as any;
            
            if (refreshError) {
              console.warn('Token-Refresh fehlgeschlagen:', refreshError);
              
              if (showUserFeedback && !hasShownUserFeedback && attempt === maxRetries - 1) {
                hasShownUserFeedback = true;
                console.log('🔄 Token-Refresh fehlgeschlagen nach mehreren Versuchen');
                
                // Fallback: Zeige Session Recovery Toast
                  showSessionRecoveryToast(() => {
                    withRetry(operation, 1, false).catch(console.error);
                  });
              }
            } else {
              console.log('Token erfolgreich refreshed, wiederhole Operation...');
              continue;
            }
          } catch (refreshError) {
            console.warn('Token-Refresh-Fehler:', refreshError);
          }
        }
      }
      
      if (attempt === maxRetries) {
        throw lastError;
      }
      
      await new Promise(resolve => setTimeout(resolve, 1000 * (attempt + 1)));
    }
  }
  
  throw lastError;
};

// Spezielle Funktion für Tab-Wechsel-Recovery
export const handleTabVisibilityChange = async () => {
  if (document.hidden) return true; // Nur wenn Tab wieder sichtbar wird
  
  try {
    console.log('🔍 Tab wurde sichtbar - prüfe Session-Gültigkeit...');
    
    // Prüfe aktuelle Session mit Timeout
    const sessionPromise = supabase.auth.getSession();
    const timeoutPromise = new Promise((_, reject) => 
      setTimeout(() => reject(new Error('Tab recovery session check timeout')), 5000)
    );
    
    const { data: { session }, error } = await Promise.race([
      sessionPromise,
      timeoutPromise
    ]) as any;
    
    if (error) {
      console.warn('❌ Session-Fehler nach Tab-Wechsel:', error);
      
      // Versuche automatischen Token-Refresh mit Timeout
      const refreshPromise = supabase.auth.refreshSession();
      const refreshTimeoutPromise = new Promise((_, reject) => 
        setTimeout(() => reject(new Error('Tab recovery refresh timeout')), 3000)
      );
      
      const { error: refreshError } = await Promise.race([
        refreshPromise,
        refreshTimeoutPromise
      ]) as any;
      
      if (refreshError) {
        console.error('❌ Token-Refresh nach Tab-Wechsel fehlgeschlagen:', refreshError);
        throw refreshError;
      } else {
        console.log('✅ Token nach Tab-Wechsel erfolgreich refreshed');
      }
    } else if (!session) {
      console.warn('❌ Keine Session nach Tab-Wechsel gefunden');
      throw new Error('No session found');
    } else {
      console.log('✅ Session nach Tab-Wechsel ist gültig');
    }
    
    return true;
  } catch (error) {
    console.error('❌ Tab-Wechsel Session-Recovery fehlgeschlagen:', error);
    
    // Bei Timeout-Fehlern spezielle Behandlung
    if (error.message?.includes('timeout')) {
      console.log('⏰ Tab-Recovery-Timeout - beende Recovery');
    }
    
    return false;
  }
};

// Event-Listener für Tab-Wechsel
if (typeof window !== 'undefined') {
  document.addEventListener('visibilitychange', () => {
    if (!document.hidden) {
      handleTabVisibilityChange();
    }
  });
}